import {useState} from "react";
import {Badge, Button, Card} from "react-bootstrap";
import {Link} from "react-router";
import {SupportApi} from "../../api/SupportApi";
import {SupportTicketViewModel} from "../../api/models";
import {ErrorAlert} from "../../components/ErrorAlert";
import {ConfirmModal} from "../../components/ConfirmModal";

// Only http(s) links are made clickable: the API checks them, but they're still the requester's words.
const safeHref = (url: string) => /^https?:\/\//i.test(url) ? url : undefined;

function ExternalLink({url}: { url: string }) {
    const href = safeHref(url);
    return href ? <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="text-break">{url}</a> : <span className="text-break">{url}</span>;
}

// For administrators, on a press inquiry: what the requester said about themselves, and when they ask for a review
// license, what helps decide whether they are who they say. The strongest sign is an address at the outlet's own
// domain; without one, ask them to confirm through a channel the outlet controls (its published contact address, the
// channel's business email, or a phrase posted on the channel), or offer the free demo instead. Issue Review License
// gives them one for the inquiry's product, for 30 days (the API resolves the ticket and emails them the key).
export function PressRequestCard({ticket, onChanged}: { ticket: SupportTicketViewModel, onChanged(ticket: SupportTicketViewModel): void }) {
    const request = ticket.press!;
    const email = ticket.customerEmail;
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const issue = async () => {
        setConfirming(false);
        setBusy(true);
        setError(undefined);
        try {
            await SupportApi.issueReviewLicense(ticket.id);
            onChanged(await SupportApi.get(ticket.id));
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };
    return (
        <Card className="mb-4" data-testid="press-request">
            <Card.Header as="h2" className="h6 mb-0">
                Press inquiry{request.reviewLicense && <Badge bg="warning" text="dark" className="ms-2">Review license requested</Badge>}
            </Card.Header>
            <Card.Body>
                <dl className="mb-0 support-details">
                    <dt>Publication</dt>
                    <dd>{request.publication}</dd>
                    {request.outletUrl && <><dt>Outlet</dt><dd><ExternalLink url={request.outletUrl}/></dd></>}
                    {request.role && <><dt>Role</dt><dd>{request.role}</dd></>}
                </dl>
                {request.reviewLicense && (
                    <>
                        <h3 className="h6 mt-3">Checks</h3>
                        <ul className="list-unstyled small mb-0">
                            <li className="mb-1">
                                {request.emailMatchesOutlet
                                    ? <><i className="bi bi-check-circle-fill text-success me-1" aria-hidden="true"></i>The email address is at the outlet's domain.</>
                                    : <><i className="bi bi-exclamation-triangle-fill text-warning me-1" aria-hidden="true"></i>The email address isn't at the outlet's domain: ask them to confirm through a channel the outlet controls, or offer the free demo.</>}
                            </li>
                            <li>
                                {ticket.ownerUsername
                                    ? <>Sent signed in as <strong>{ticket.ownerUsername}</strong>: look them up under <Link to="/admin/customers">Customers</Link> for their licenses and tickets.</>
                                    : ticket.existingAccount
                                        ? <>{email} already has an account, <strong>{ticket.existingAccount}</strong>: look it up under <Link to="/admin/customers">Customers</Link> for its licenses and tickets.</>
                                        : <>{email} has no account yet.</>}
                            </li>
                        </ul>
                    </>
                )}
                <div className="mt-3"><ErrorAlert error={error} onClose={() => setError(undefined)}/></div>
                {request.reviewLicenseId ? (
                    <p className="small mt-3 mb-0">
                        <i className="bi bi-check-circle-fill text-success me-1" aria-hidden="true"></i>
                        Review license issued: <Link to={`/admin/licenses/${encodeURIComponent(request.reviewLicenseId)}`}>view license</Link>.
                    </p>
                ) : ticket.productId ? (
                    <Button variant={request.reviewLicense ? 'primary' : 'outline-primary'} size="sm" className="mt-3" disabled={busy} onClick={() => setConfirming(true)}>
                        <i className="bi bi-key me-1" aria-hidden="true"></i>Issue Review License
                    </Button>
                ) : (
                    <p className="small text-body-secondary mt-3 mb-0">No product was chosen, so there's no review license to issue.</p>
                )}
            </Card.Body>
            <ConfirmModal show={confirming} title={`Issue a review license for ${ticket.productName}?`} confirmLabel="Issue license" busy={busy}
                          onConfirm={() => void issue()} onCancel={() => setConfirming(false)}>
                <p>
                    It goes to {ticket.ownerUsername || ticket.existingAccount || `a new account for ${email}`}, works on two computers for 30 days,
                    and is revoked then unless you extend or keep it.
                </p>
                <p className="mb-0 text-body-secondary">The reviewer is emailed the key, the ticket gets a note, and it's resolved.</p>
            </ConfirmModal>
        </Card>
    );
}
